import {useMemo} from "react";
import {AnankeTempInlineEditableSelect} from "~/components/anankeTemp/anankeTempInlineEditableSelect";
import {useBtlActivities} from "~/contexts/workspaceBtlActivitiesContext";
import {useWorkspace} from "~/contexts/workspaceContext";
import {useOrganizationalUnits} from "~/contexts/organizationalUnitsContext";
import {useOrganizationalUnitTemplates} from "~/contexts/workspaceContext";
import {useProductCatalogue} from "~/contexts/productCatalogueContext";
import {useUsers} from "~/contexts/workspaceUsersContext";
import {useWorkspaceUserTemplates as useUserTemplates} from "~/contexts/workspaceContext";
import type {OrganizationalUnit} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnit";
import type {OrganizationalUnitTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnitTemplate";
import {isEmotoradWorkspace} from "~/domainModels/workspaceSpecific/emotorad";
import {useOnlyEventContainerTemplate} from "~/contexts/workspaceEventContainerTemplatesContext";
import {AnankeSelect} from "@vesta/ananke/components/anankeSelect";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {getUserDisplayName} from "~/utilities/utilities";
import {processOttopilotStringTemplate} from "@vesta/vos-types/modules/workspaceLevel/domain/OttopilotStringTemplate";
import type {BtlActivity} from "~/domainModels/btlActivities";
import {getProductCatalogueDisplayName} from "~/utilities/productCatalogueDisplayName";

export function UserPicker({
    value,
    setValue,
    organizationalUnitId,
}: {
    value: Uuid | null;
    setValue: (value: Uuid | null) => void;
    organizationalUnitId?: Uuid | null;
}) {
    const users = useUsers();
    const userTemplates = useUserTemplates();
    const {workspace, platformUserDetails} = useWorkspace();
    const platformUserDetailsByUserId = useMemo(
        () => new Map(platformUserDetails.map((platformUserDetail) => [platformUserDetail.userId, platformUserDetail])),
        [platformUserDetails],
    );

    const EMOTORAD__USER__DEALERSHIP__COLUMN_ID = "85bd11a0-6418-5c03-9764-b3f036d36bdb" as Uuid;
    const filteredUsers = useMemo(() => {
        if (organizationalUnitId === undefined || organizationalUnitId === null || isEmotoradWorkspace(workspace) !== true) {
            return users;
        }
        return users.filter((user) => {
            const userOuId = user.additionalColumnValues[EMOTORAD__USER__DEALERSHIP__COLUMN_ID];
            return userOuId === organizationalUnitId;
        });
    }, [users, organizationalUnitId, workspace]);

    return (
        <AnankeSelect
            value={
                value === null
                    ? null
                    : {
                          value: value,
                          label: getUserDisplayName(value, users, userTemplates, platformUserDetailsByUserId),
                      }
            }
            onChange={(newValue: {value: string; label: string} | null) => {
                setValue(newValue !== null ? (newValue.value as Uuid) : null);
            }}
            options={filteredUsers.map((user) => {
                return {
                    value: user.userId,
                    label: getUserDisplayName(user.userId, users, userTemplates, platformUserDetailsByUserId),
                };
            })}
        />
    );
}

export function InlineUserPicker({
    value,
    setValue,
    organizationalUnitId,
}: {
    value: Uuid | null;
    setValue: (value: Uuid | null) => void;
    organizationalUnitId?: Uuid | null;
}) {
    const users = useUsers();
    const userTemplates = useUserTemplates();
    const {workspace, platformUserDetails} = useWorkspace();
    const platformUserDetailsByUserId = useMemo(
        () => new Map(platformUserDetails.map((platformUserDetail) => [platformUserDetail.userId, platformUserDetail])),
        [platformUserDetails],
    );

    const EMOTORAD__USER__DEALERSHIP__COLUMN_ID = "85bd11a0-6418-5c03-9764-b3f036d36bdb" as Uuid;
    const filteredUsers = useMemo(() => {
        if (organizationalUnitId === undefined || organizationalUnitId === null || isEmotoradWorkspace(workspace) !== true) {
            return users;
        }
        return users.filter((user) => {
            const userOuId = user.additionalColumnValues[EMOTORAD__USER__DEALERSHIP__COLUMN_ID];
            return userOuId === organizationalUnitId;
        });
    }, [users, organizationalUnitId, workspace]);

    return (
        <AnankeTempInlineEditableSelect
            defaultValue={{
                value: value ?? "",
                label: value === null ? "" : getUserDisplayName(value, users, userTemplates, platformUserDetailsByUserId),
            }}
            onConfirm={(newValue: {value: string; label: string} | null) => {
                setValue(newValue !== null ? (newValue.value as Uuid) : null);
            }}
            options={filteredUsers.map((user) => {
                return {
                    value: user.userId,
                    label: getUserDisplayName(user.userId, users, userTemplates, platformUserDetailsByUserId),
                };
            })}
            placeholder="-"
            readViewFitContainerWidth={true}
            // hideActionButtons={true}
        />
    );
}

export function BtlActivityPicker({value, setValue, placeholder}: {value: Uuid | null; setValue: (value: Uuid | null) => void; placeholder?: string}) {
    const btlActivities = useBtlActivities() as Array<BtlActivity>;
    const selectedActivity = btlActivities.find((a) => a.id === value);

    const options = useMemo(() => {
        const mapped = btlActivities.map((activity) => ({
            value: activity.id,
            label: activity.title,
        }));
        if (value === null) {
            return mapped;
        }
        if (mapped.some((o) => o.value === value)) {
            return mapped;
        }
        // Atlaskit/react-select clears the value when it is missing from options (e.g. list refetching
        // or the activity briefly absent). Keep a synthetic option so the selection survives.
        return [
            {
                value,
                label: selectedActivity !== undefined ? selectedActivity.title : "Selected beat",
            },
            ...mapped,
        ];
    }, [btlActivities, value, selectedActivity]);

    return (
        <AnankeSelect
            placeholder={placeholder}
            value={
                value === null
                    ? null
                    : {
                          value: value,
                          label: selectedActivity?.title ?? "?",
                      }
            }
            onChange={(newValue: {value: string; label: string} | null) => setValue(newValue !== null ? (newValue.value as Uuid) : null)}
            options={options}
        />
    );
}

export function InlineBtlActivityPicker({value, setValue}: {value: Uuid | null; setValue: (value: Uuid | null) => void}) {
    const btlActivities = useBtlActivities() as Array<BtlActivity>;
    const selectedActivity = btlActivities.find((a) => a.id === value);

    return (
        <AnankeTempInlineEditableSelect
            defaultValue={{
                value: value === null ? "" : value,
                label: value === null ? "" : (selectedActivity?.title ?? "?"),
            }}
            onConfirm={(newValue: {value: string; label: string} | null) => setValue(newValue !== null ? (newValue.value as Uuid) : null)}
            options={btlActivities.map((activity) => ({
                value: activity.id as string,
                label: activity.title as string,
            }))}
            placeholder="-"
            readViewFitContainerWidth={true}
        />
    );
}

export function ProductPicker({
    value,
    setValue,
    placeholder = "Select",
    productCatalogueTemplateId,
    allowedProductIds,
}: {
    value: Uuid | null;
    setValue: (value: Uuid | null) => void;
    placeholder?: string;
    productCatalogueTemplateId?: Uuid;
    /** When set, only these product rows (plus the current {@link value} if missing) appear in the menu. */
    allowedProductIds?: ReadonlySet<Uuid>;
}) {
    const allProducts = useProductCatalogue() ?? [];
    let products = productCatalogueTemplateId === undefined ? allProducts : allProducts.filter((p) => p.templateId === productCatalogueTemplateId);
    if (allowedProductIds !== undefined) {
        products = products.filter((p) => allowedProductIds.has(p.id) === true || (value !== null && p.id === value));
    }
    const selectedProduct = products.find((p) => p.id === value) ?? (value === null ? undefined : allProducts.find((p) => p.id === value));

    return (
        <AnankeSelect
            placeholder={placeholder}
            value={
                value === null
                    ? null
                    : (() => {
                          const EMOTORAD__PRODUCT_CATALOGUE__COLOR_NAME__COLUMN_ID = "ec16634a-156b-48eb-9d32-1364f0b80f42" as Uuid;
                          const colorName = selectedProduct?.additionalColumnValues[EMOTORAD__PRODUCT_CATALOGUE__COLOR_NAME__COLUMN_ID];
                          const label =
                              typeof colorName === "string" && colorName.trim() !== ""
                                  ? colorName
                                  : selectedProduct === undefined
                                    ? "?"
                                    : getProductCatalogueDisplayName(selectedProduct, []);
                          return {value, label};
                      })()
            }
            onChange={(newValue: {value: string; label: string} | null) => setValue(newValue !== null ? (newValue.value as Uuid) : null)}
            options={products.map((product) => {
                const EMOTORAD__PRODUCT_CATALOGUE__COLOR_NAME__COLUMN_ID = "ec16634a-156b-48eb-9d32-1364f0b80f42" as Uuid;
                const colorName = product.additionalColumnValues[EMOTORAD__PRODUCT_CATALOGUE__COLOR_NAME__COLUMN_ID];
                const label =
                    typeof colorName === "string" && colorName.trim() !== ""
                        ? colorName
                        : getProductCatalogueDisplayName(product, []);
                return {
                    value: product.id,
                    label,
                };
            })}
        />
    );
}

export function InlineProductPicker({
    value,
    setValue,
    productCatalogueTemplateId,
    allowedProductIds,
}: {
    value: Uuid | null;
    setValue: (value: Uuid | null) => void;
    productCatalogueTemplateId?: Uuid;
    allowedProductIds?: ReadonlySet<Uuid>;
}) {
    const allProducts = useProductCatalogue() ?? [];
    let products = productCatalogueTemplateId === undefined ? allProducts : allProducts.filter((p) => p.templateId === productCatalogueTemplateId);
    if (allowedProductIds !== undefined) {
        products = products.filter((p) => allowedProductIds.has(p.id) === true || (value !== null && p.id === value));
    }
    const selectedProduct = products.find((p) => p.id === value) ?? (value === null ? undefined : allProducts.find((p) => p.id === value));

    return (
        <AnankeTempInlineEditableSelect
            defaultValue={(() => {
                if (value === null) {
                    return {value: "", label: ""};
                }
                const EMOTORAD__PRODUCT_CATALOGUE__COLOR_NAME__COLUMN_ID = "ec16634a-156b-48eb-9d32-1364f0b80f42" as Uuid;
                const colorName = selectedProduct?.additionalColumnValues[EMOTORAD__PRODUCT_CATALOGUE__COLOR_NAME__COLUMN_ID];
                const label =
                    typeof colorName === "string" && colorName.trim() !== ""
                        ? colorName
                        : selectedProduct === undefined
                          ? "?"
                          : getProductCatalogueDisplayName(selectedProduct, []);
                return {value, label};
            })()}
            onConfirm={(newValue: {value: string; label: string} | null) => setValue(newValue !== null ? (newValue.value as Uuid) : null)}
            options={products.map((product) => {
                const EMOTORAD__PRODUCT_CATALOGUE__COLOR_NAME__COLUMN_ID = "ec16634a-156b-48eb-9d32-1364f0b80f42" as Uuid;
                const colorName = product.additionalColumnValues[EMOTORAD__PRODUCT_CATALOGUE__COLOR_NAME__COLUMN_ID];
                const label =
                    typeof colorName === "string" && colorName.trim() !== ""
                        ? colorName
                        : getProductCatalogueDisplayName(product, []);
                return {
                    value: product.id,
                    label: label as string,
                };
            })}
            placeholder="-"
            readViewFitContainerWidth={true}
        />
    );
}

export function OrganizationalUnitPicker({
    value,
    setValue,
    placeholder = "Select",
}: {
    value: Uuid | null;
    setValue: (value: Uuid | null) => void;
    placeholder?: string;
}) {
    const organizationalUnits = useOrganizationalUnits();
    const organizationalUnitTemplates = useOrganizationalUnitTemplates();
    const eventContainerConfiguration = useOnlyEventContainerTemplate().configuration;

    const selectedOrganizationalUnit: OrganizationalUnit | undefined =
        value === null ? undefined : organizationalUnits.find((organizationalUnit) => organizationalUnit.id === value);
    const selectedOrganizationalUnitTemplate: OrganizationalUnitTemplate | undefined =
        selectedOrganizationalUnit === undefined
            ? undefined
            : organizationalUnitTemplates.find(
                  (organizationalUnitTemplate) => organizationalUnitTemplate.id === selectedOrganizationalUnit.templateId,
              );

    return (
            <AnankeSelect
                placeholder={placeholder}
                menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                menuPosition="fixed"
                styles={{menuPortal: (base: Record<string, unknown>) => ({...base, zIndex: 9999})}}
            value={
                value === null
                    ? null
                    : {
                          value: value,
                          label:
                              selectedOrganizationalUnit === undefined || selectedOrganizationalUnitTemplate === undefined
                                  ? "?"
                                  : processOttopilotStringTemplate(
                                        selectedOrganizationalUnitTemplate.instanceDisplayNameTemplate,
                                        selectedOrganizationalUnit.additionalColumnValues,
                                    ),
                      }
            }
            onChange={(newValue: {value: string; label: string} | null) => {
                setValue(newValue !== null ? (newValue.value as Uuid) : null);
            }}
            options={organizationalUnits
                .filter((organizationalUnit) => {
                    return eventContainerConfiguration.validOrganizationUnitTemplateIds.includes(organizationalUnit.templateId);
                })
                .map((organizationalUnit) => {
                    const organizationalUnitTemplate = organizationalUnitTemplates.find(
                        (organizationalUnitTemplate) => organizationalUnitTemplate.id === organizationalUnit.templateId,
                    );
                    if (organizationalUnitTemplate === undefined) {
                        throw new Error("organizationalUnitTemplate not found");
                    }

                    return {
                        value: organizationalUnit.id,
                        label: organizationalUnit.additionalColumnValues[
                            organizationalUnitTemplate.additionalColumns.find(
                                (column) => column.displayName.toLocaleLowerCase() === "name" || column.displayName.toLocaleLowerCase() === "displayname",
                            )!.id
                        ] as string,
                    };
                })}
        />
    );
}

export function InlineOrganizationalUnitPicker({value, setValue}: {value: Uuid | null; setValue: (value: Uuid | null) => void}) {
    const organizationalUnits = useOrganizationalUnits();
    const organizationalUnitTemplates = useOrganizationalUnitTemplates();
    const eventContainerConfiguration = useOnlyEventContainerTemplate().configuration;

    const selectedOrganizationalUnit: OrganizationalUnit | undefined =
        value === null ? undefined : organizationalUnits.find((organizationalUnit) => organizationalUnit.id === value);
    const selectedOrganizationalUnitTemplate: OrganizationalUnitTemplate | undefined =
        selectedOrganizationalUnit === undefined
            ? undefined
            : organizationalUnitTemplates.find(
                  (organizationalUnitTemplate) => organizationalUnitTemplate.id === selectedOrganizationalUnit.templateId,
              );

    return (
        <AnankeTempInlineEditableSelect
            defaultValue={{
                value: value ?? "",
                label:
                    value === null
                        ? ""
                        : selectedOrganizationalUnit === undefined || selectedOrganizationalUnitTemplate === undefined
                          ? "?"
                          : processOttopilotStringTemplate(
                                selectedOrganizationalUnitTemplate.instanceDisplayNameTemplate,
                                selectedOrganizationalUnit.additionalColumnValues,
                            ),
            }}
            onConfirm={(newValue: {value: string; label: string} | null) => {
                setValue(newValue !== null ? (newValue.value as Uuid) : null);
            }}
            options={organizationalUnits
                .filter((organizationalUnit) => {
                    return eventContainerConfiguration.validOrganizationUnitTemplateIds.includes(organizationalUnit.templateId);
                })
                .map((organizationalUnit) => {
                    const organizationalUnitTemplate = organizationalUnitTemplates.find(
                        (organizationalUnitTemplate) => organizationalUnitTemplate.id === organizationalUnit.templateId,
                    );
                    if (organizationalUnitTemplate === undefined) {
                        throw new Error("organizationalUnitTemplate not found");
                    }

                    return {
                        value: organizationalUnit.id,
                        label: organizationalUnit.additionalColumnValues[
                            organizationalUnitTemplate.additionalColumns.find(
                                (column) => column.displayName.toLocaleLowerCase() === "name" || column.displayName.toLocaleLowerCase() === "displayname",
                            )!.id
                        ] as string,
                    };
                })}
            placeholder="-"
            readViewFitContainerWidth={true}
            // hideActionButtons={true}
        />
    );
}
